/**
 * Pure Helfer fuer die Daueraufträge-Liste (Phase 8, Plan 06). Kein React.
 * Wire-Typen spiegeln core/backend/src/agents/dauerauftraege/dauerauftragTypen.ts von Hand.
 */
import { sichereKarte, type Karte } from '../../components/karten/kartenSchema';

export type DauerauftragDringlichkeit = 'normal' | 'wichtig' | 'dringend';
export type DauerauftragStatus = 'vorgeschlagen' | 'aktiv' | 'pausiert' | 'geloescht';
export type Meldeweg = 'dashboard' | 'kopfzeile' | 'mail';

export interface DauerauftragEintrag {
  id: string;
  titel: string;
  taktKlartext: string;
  darstellung: string;
  dringlichkeit: DauerauftragDringlichkeit;
  status: DauerauftragStatus;
  letzterLaufAt: string | null;
  naechsterLaufAt: string | null;
  letzteKarte: Karte | null;
  letzterMeldeweg: Meldeweg | null;
  fehlerFolge: number;
}

export interface HinweisEintrag {
  id: string;
  titel: string;
  karte: Karte;
  letzterLaufAt: string | null;
}

const STATUS_WERTE: DauerauftragStatus[] = ['vorgeschlagen', 'aktiv', 'pausiert', 'geloescht'];
const DRINGLICHKEITEN: DauerauftragDringlichkeit[] = ['normal', 'wichtig', 'dringend'];
const MELDEWEGE: Meldeweg[] = ['dashboard', 'kopfzeile', 'mail'];

function textOderNull(v: unknown): string | null {
  return typeof v === 'string' && v !== '' ? v : null;
}

export function normalisiereDauerauftrag(roh: unknown): DauerauftragEintrag | null {
  if (!roh || typeof roh !== 'object') return null;
  const r = roh as Record<string, unknown>;
  const id = typeof r.id === 'string' || typeof r.id === 'number' ? String(r.id) : '';
  if (!id) return null;
  if (!STATUS_WERTE.includes(r.status as DauerauftragStatus)) return null;
  const dringlichkeit = DRINGLICHKEITEN.includes(r.dringlichkeit as DauerauftragDringlichkeit)
    ? (r.dringlichkeit as DauerauftragDringlichkeit)
    : 'normal';
  const meldeweg = MELDEWEGE.includes(r.letzterMeldeweg as Meldeweg) ? (r.letzterMeldeweg as Meldeweg) : null;
  const fehler = Number(r.fehlerFolge);
  return {
    id,
    titel: typeof r.titel === 'string' && r.titel.trim() ? r.titel : 'Ohne Titel',
    taktKlartext: typeof r.taktKlartext === 'string' ? r.taktKlartext : '',
    darstellung: typeof r.darstellung === 'string' ? r.darstellung : '',
    dringlichkeit,
    status: r.status as DauerauftragStatus,
    letzterLaufAt: textOderNull(r.letzterLaufAt),
    naechsterLaufAt: textOderNull(r.naechsterLaufAt),
    letzteKarte: r.letzteKarte ? sichereKarte(r.letzteKarte) : null,
    letzterMeldeweg: meldeweg,
    fehlerFolge: Number.isFinite(fehler) && fehler > 0 ? Math.floor(fehler) : 0,
  };
}

export function normalisiereHinweis(roh: unknown): HinweisEintrag | null {
  if (!roh || typeof roh !== 'object') return null;
  const r = roh as Record<string, unknown>;
  const id = typeof r.id === 'string' || typeof r.id === 'number' ? String(r.id) : '';
  if (!id || !r.karte) return null;
  return {
    id,
    titel: typeof r.titel === 'string' ? r.titel : '',
    karte: sichereKarte(r.karte),
    letzterLaufAt: textOderNull(r.letzterLaufAt),
  };
}

export function statusChip(status: DauerauftragStatus): { label: string; ton: 'aktiv' | 'pausiert' | 'wartet' } {
  switch (status) {
    case 'aktiv': return { label: 'Aktiv', ton: 'aktiv' };
    case 'pausiert': return { label: 'Pausiert', ton: 'pausiert' };
    default: return { label: 'Wartet auf Bestätigung', ton: 'wartet' };
  }
}

export function dringlichkeitLabel(d: DauerauftragDringlichkeit): string {
  if (d === 'wichtig') return 'Wichtig · Kopfzeile';
  if (d === 'dringend') return 'Dringend · Mail';
  return 'Normal';
}

export interface Aktionen {
  pausieren: boolean;
  fortsetzen: boolean;
  loeschen: boolean;
  jetzt: boolean;
  bestaetigen: boolean;
}

export function verfuegbareAktionen(e: Pick<DauerauftragEintrag, 'status'>): Aktionen {
  const aus: Aktionen = { pausieren: false, fortsetzen: false, loeschen: false, jetzt: false, bestaetigen: false };
  if (e.status === 'aktiv') return { ...aus, pausieren: true, jetzt: true, loeschen: true };
  if (e.status === 'pausiert') return { ...aus, fortsetzen: true, jetzt: true, loeschen: true };
  if (e.status === 'vorgeschlagen') return { ...aus, bestaetigen: true, loeschen: true };
  return aus;
}

const TAGE = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];

/** Deutsches Kurzformat in Europe/Berlin, z. B. "Mo 13.10. 08:00"; "—" bei null/ungueltig. */
export function zeitText(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const teile = new Intl.DateTimeFormat('de-DE', {
    timeZone: 'Europe/Berlin', weekday: 'short', day: '2-digit', month: '2-digit',
    hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  }).formatToParts(d);
  const w = (t: string) => teile.find((p) => p.type === t)?.value ?? '';
  const wochentag = w('weekday').replace('.', '');
  const kurz = TAGE.find((t) => wochentag.startsWith(t)) ?? wochentag;
  return `${kurz} ${w('day')}.${w('month')}. ${w('hour')}:${w('minute')}`;
}

export function fehlerHinweis(e: Pick<DauerauftragEintrag, 'status' | 'fehlerFolge'>): string | null {
  if (e.status === 'pausiert' && e.fehlerFolge >= 1) {
    return `Pausiert nach ${e.fehlerFolge} ${e.fehlerFolge === 1 ? 'Fehler' : 'Fehlern'}`;
  }
  return null;
}
